import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { EventDetail } from "@/types/event";
import { getServiceSegments } from "@/types/event";
import type { FormCoveredBy, FormTiny, FormTinyWithStatus } from "@/types/forms/form";
import type { PatientSubmissionTableItem, PatientSubmissionsTable } from "@/types/forms/form";
import { formQueryOptions } from "@/api/forms/form-query";
export interface FormStatusItem {
  form: FormTiny;
  /**
   * The submission to open: the patient's own, or the older form's when this
   * form is covered by one it replaced.
   */
  submission: PatientSubmissionTableItem | undefined;
  isCompleted: boolean;
  /** When the counted submission was made. */
  completedDate: string | undefined;
  /** Older forms whose submissions count for this one. Empty when the patient filled it. */
  coveredBy: FormCoveredBy[];
}
export interface FormStatusList {
  items: FormStatusItem[];
  hasRequiredForms: boolean;
  /** True while the server is still checking forms covered by older ones. */
  isCoverageLoading?: boolean;
}
function requiredFormsFor(event: EventDetail, activeForms?: Set<string>): FormTiny[] {
  const forms = getServiceSegments(event.segments || []).flatMap(es => (es.service.forms ?? []).filter(f => !!f && !f.isArchived && (!activeForms || activeForms.has(f.id))));
  return Array.from(new Map(forms.map(form => [form.id, form])).values());
}
function completedSubmission(submissions: PatientSubmissionTableItem[], formId: string): PatientSubmissionTableItem | undefined {
  return submissions.find(sub => sub.formId === formId && sub.isCompleted && !sub.isExpired);
}

/**
 * Computes form completion status for an appointment by matching
 * required forms (from the event's service config) against patient submissions.
 *
 * Deduplicates required forms by ID and checks both isCompleted and isExpired.
 * A newer draft never hides an older completed submission of the same form.
 *
 * `coverage` is the server's status for forms the patient has not filled
 * themselves: a form counts as done when the older forms it replaced are done.
 *
 * `hasRequiredForms` is derived from event segments only and is stable during
 * submission loading, making it safe to use for conditional rendering.
 *
 * `activeForms` optionally restricts required forms to only those still active
 * in the system (i.e. not deleted/archived).
 */
export function useFormStatusList(event: EventDetail, formSubmissions: PatientSubmissionsTable | undefined, activeForms?: Set<string>, coverage?: FormTinyWithStatus[]): FormStatusList {
  return useMemo(() => {
    const requiredForms = requiredFormsFor(event, activeForms);
    const hasRequiredForms = requiredForms.length > 0;
    if (!formSubmissions?.data || !hasRequiredForms) {
      return {
        items: [],
        hasRequiredForms
      };
    }
    const submissions = formSubmissions.data;
    return {
      hasRequiredForms,
      items: requiredForms.map((form): FormStatusItem => {
        const own = completedSubmission(submissions, form.id);
        if (own) {
          return {
            form,
            submission: own,
            isCompleted: true,
            completedDate: own.submissionDate,
            coveredBy: []
          };
        }
        const status = coverage?.find(s => s.id === form.id);
        const coveredBy = status?.coveredBy ?? [];
        if (status?.completed && coveredBy.length > 0) {
          return {
            form,
            submission: completedSubmission(submissions, coveredBy[0].id),
            isCompleted: true,
            completedDate: status.completedAt ?? undefined,
            coveredBy
          };
        }
        return {
          form,
          submission: submissions.find(sub => sub.formId === form.id),
          isCompleted: false,
          completedDate: undefined,
          coveredBy: []
        };
      })
    };
  }, [event, formSubmissions?.data, activeForms, coverage]);
}

/**
 * `useFormStatusList` plus the server's check of forms the patient hasn't filled
 * themselves, so a form covered by older forms it replaced shows as done. Only
 * asks the server when some required form has no completed submission.
 */
export function useAppointmentFormStatusList(event, patientId, formSubmissions, activeForms) {
  const $ = _c(15);
  formSubmissions?.data;
  let t0;
  bb0: {
    if (!formSubmissions?.data) {
      let t1;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = [];
        $[0] = t1;
      } else {
        t1 = $[0];
      }
      t0 = t1;
      break bb0;
    }
    const submissions = formSubmissions.data;
    let t1;
    if ($[1] !== activeForms || $[2] !== event || $[3] !== submissions) {
      let t2;
      if ($[5] !== submissions) {
        t2 = form => !completedSubmission(submissions, form.id);
        $[5] = submissions;
        $[6] = t2;
      } else {
        t2 = $[6];
      }
      t1 = requiredFormsFor(event, activeForms).filter(t2).map(_temp).sort();
      $[1] = activeForms;
      $[2] = event;
      $[3] = submissions;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = t1;
  }
  const unfilledPaths = t0;
  let t1;
  if ($[7] !== patientId || $[8] !== unfilledPaths) {
    t1 = formQueryOptions.statuses(patientId ?? "", unfilledPaths);
    $[7] = patientId;
    $[8] = unfilledPaths;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== t1) {
    t2 = {
      ...t1
    };
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const {
    data: coverage,
    isLoading: isCoverageLoading
  } = useQuery(t2);
  const list = useFormStatusList(event, formSubmissions, activeForms, coverage);
  let t3;
  if ($[12] !== isCoverageLoading || $[13] !== list) {
    t3 = {
      ...list,
      isCoverageLoading
    };
    $[12] = isCoverageLoading;
    $[13] = list;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
function _temp(form_0) {
  return form_0.pathName;
}